import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CalendarTodayRounded,
  CheckCircleRounded,
  FactCheckRounded,
  RefreshRounded,
  ScheduleRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { getProperties, type PropertyRecord } from "../api/properties";
import { getRooms, type RoomRecord } from "../api/rooms";
import {
  createKitchenInspection,
  createLaundryInspection,
  createRoomInspection,
  getKitchenInspections,
  getLaundryInspections,
  getRoomInspections,
  type KitchenInspectionRecord,
  type LaundryInspectionRecord,
  type RoomInspectionRecord,
} from "../api/inspections";
import {
  InspectionModal,
  type InspectionFormState,
  type InspectionType,
} from "../components/InspectionModal";
import { useNotifications } from "../components/NotificationProvider";
import { formatDate } from "@/utils/formatDate";

const createEmptyFormState = (): InspectionFormState => ({
  targetId: "",
  inspection_date: new Date().toISOString().slice(0, 10),
  result: "pass",
  comments: "",
});

const inspectionLabels: Record<InspectionType, string> = {
  room: "Room inspection",
  kitchen: "Kitchen inspection",
  laundry: "Laundry inspection",
};

const inspectionCadence: Record<InspectionType, "Monthly" | "Weekly"> = {
  room: "Monthly",
  kitchen: "Weekly",
  laundry: "Weekly",
};

type InspectionFilter = InspectionType | "all";
type RoomCoverageFilter = "all" | "inspected" | "pending";
type InspectionDueStatus = "overdue" | "due_soon" | "on_track";

type InspectionDueSummary = {
  status: InspectionDueStatus;
  label: string;
  nextDueDate: string;
  daysUntilDue: number;
};

export function InspectionsPage() {
  const [inspectionType, setInspectionType] = useState<InspectionType>("room");
  const [inspectionFilter, setInspectionFilter] =
    useState<InspectionFilter>("all");
  const [roomCoverageFilter, setRoomCoverageFilter] =
    useState<RoomCoverageFilter>("all");
  const [roomSearch, setRoomSearch] = useState("");
  const [selectedRoom, setSelectedRoom] = useState<RoomRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [roomInspections, setRoomInspections] = useState<
    RoomInspectionRecord[]
  >([]);
  const [kitchenInspections, setKitchenInspections] = useState<
    KitchenInspectionRecord[]
  >([]);
  const [laundryInspections, setLaundryInspections] = useState<
    LaundryInspectionRecord[]
  >([]);
  const [forms, setForms] = useState<
    Record<InspectionType, InspectionFormState>
  >({
    room: createEmptyFormState(),
    kitchen: createEmptyFormState(),
    laundry: createEmptyFormState(),
  });
  const { showAlert, showSnackbar } = useNotifications();

  const activeForm = forms[inspectionType];
  const combinedInspectionHistory = useMemo(() => {
    const entries = [
      ...roomInspections.map((inspection) => ({
        ...inspection,
        type: "room" as const,
        targetLabel: rooms.find((room) => room.id === inspection.room_id)
          ? `Room ${rooms.find((room) => room.id === inspection.room_id)?.room_number}`
          : `Room #${inspection.room_id}`,
      })),
      ...kitchenInspections.map((inspection) => ({
        ...inspection,
        type: "kitchen" as const,
        targetLabel: properties.find(
          (property) => property.id === inspection.property_id,
        )
          ? `Property ${properties.find((property) => property.id === inspection.property_id)?.property_name}`
          : `Property #${inspection.property_id}`,
      })),
      ...laundryInspections.map((inspection) => ({
        ...inspection,
        type: "laundry" as const,
        targetLabel: properties.find(
          (property) => property.id === inspection.property_id,
        )
          ? `Property ${properties.find((property) => property.id === inspection.property_id)?.property_name}`
          : `Property #${inspection.property_id}`,
      })),
    ];

    return entries.sort((left, right) => {
      const leftDate = new Date(left.inspection_date).getTime();
      const rightDate = new Date(right.inspection_date).getTime();
      if (leftDate === rightDate) {
        const leftCreated = left.created_at
          ? new Date(left.created_at).getTime()
          : 0;
        const rightCreated = right.created_at
          ? new Date(right.created_at).getTime()
          : 0;
        return rightCreated - leftCreated;
      }
      return rightDate - leftDate;
    });
  }, [
    kitchenInspections,
    laundryInspections,
    properties,
    roomInspections,
    rooms,
  ]);

  const filteredInspectionHistory = useMemo(() => {
    const normalizedQuery = roomSearch.trim().toLowerCase();

    return combinedInspectionHistory.filter((inspection) => {
      const matchesType =
        inspectionFilter === "all" || inspection.type === inspectionFilter;

      const matchesSearch =
        normalizedQuery.length === 0 ||
        inspection.targetLabel.toLowerCase().includes(normalizedQuery) ||
        (inspection.type === "room" &&
          String(inspection.room_id).includes(normalizedQuery)) ||
        (inspection.type === "room" &&
          `room ${String(inspection.room_id)}`.includes(normalizedQuery));

      return matchesType && matchesSearch;
    });
  }, [combinedInspectionHistory, inspectionFilter, roomSearch]);

  const inspectionFilterCounts = useMemo(
    () => ({
      all: combinedInspectionHistory.length,
      room: roomInspections.length,
      kitchen: kitchenInspections.length,
      laundry: laundryInspections.length,
    }),
    [
      combinedInspectionHistory.length,
      kitchenInspections.length,
      laundryInspections.length,
      roomInspections.length,
    ],
  );

  const getNextDueDate = (type: InspectionType) => {
    const source =
      type === "room"
        ? roomInspections
        : type === "kitchen"
          ? kitchenInspections
          : laundryInspections;

    const latestInspection = [...source].sort((left, right) => {
      const leftValue = new Date(left.inspection_date).getTime();
      const rightValue = new Date(right.inspection_date).getTime();
      return rightValue - leftValue;
    })[0];

    const baseDate = latestInspection
      ? new Date(latestInspection.inspection_date)
      : new Date();

    const nextDate = new Date(baseDate);
    if (type === "room") {
      nextDate.setMonth(nextDate.getMonth() + 1);
    } else {
      nextDate.setDate(nextDate.getDate() + 7);
    }

    return nextDate.toISOString().slice(0, 10);
  };

  const getSuggestedFormState = (type: InspectionType) => ({
    ...forms[type],
    inspection_date: getNextDueDate(type),
  });

  const getDueSummary = (type: InspectionType): InspectionDueSummary => {
    const nextDueDate = getNextDueDate(type);
    const today = new Date();
    const targetDate = new Date(nextDueDate);
    const diffDays = Math.round(
      (targetDate.getTime() - today.setHours(0, 0, 0, 0)) / 86400000,
    );

    if (diffDays < 0) {
      return {
        status: "overdue",
        label: "Overdue",
        nextDueDate,
        daysUntilDue: diffDays,
      };
    }

    if (diffDays <= 3) {
      return {
        status: "due_soon",
        label: "Due soon",
        nextDueDate,
        daysUntilDue: diffDays,
      };
    }

    return {
      status: "on_track",
      label: "On track",
      nextDueDate,
      daysUntilDue: diffDays,
    };
  };

  const inspectionSummaries = useMemo(
    () => ({
      room: getDueSummary("room"),
      kitchen: getDueSummary("kitchen"),
      laundry: getDueSummary("laundry"),
    }),
    [roomInspections, kitchenInspections, laundryInspections],
  );

  const openModal = (
    type: InspectionType = inspectionType,
    targetId?: string,
  ) => {
    setInspectionType(type);
    setForms((current) => ({
      ...current,
      [type]: {
        ...current[type],
        ...getSuggestedFormState(type),
        targetId: targetId ?? current[type].targetId,
      },
    }));
    setIsModalOpen(true);
  };

  const roomCoverageItems = useMemo(() => {
    const latestByRoom = new Map<number, RoomInspectionRecord>();

    roomInspections.forEach((inspection) => {
      const current = latestByRoom.get(inspection.room_id);
      if (!current) {
        latestByRoom.set(inspection.room_id, inspection);
        return;
      }

      const currentDate = new Date(current.inspection_date).getTime();
      const nextDate = new Date(inspection.inspection_date).getTime();
      if (nextDate > currentDate) {
        latestByRoom.set(inspection.room_id, inspection);
      }
    });

    const today = new Date();

    return rooms
      .map((room) => {
        const latestInspection = latestByRoom.get(room.id);
        const status = latestInspection
          ? (() => {
              const dueDate = new Date(latestInspection.inspection_date);
              dueDate.setMonth(dueDate.getMonth() + 1);
              return dueDate < today ? "overdue" : "inspected";
            })()
          : "pending";

        return {
          room,
          latestInspection,
          status,
          lastInspectionDate: latestInspection?.inspection_date ?? null,
        };
      })
      .sort((left, right) => {
        const leftNumber =
          Number.parseInt(String(left.room.room_number), 10) || 0;
        const rightNumber =
          Number.parseInt(String(right.room.room_number), 10) || 0;
        return leftNumber - rightNumber;
      });
  }, [roomInspections, rooms]);

  const filteredRoomCoverage = useMemo(() => {
    const normalizedQuery = roomSearch.trim().toLowerCase();

    return roomCoverageItems.filter((item) => {
      const matchesStatus =
        roomCoverageFilter === "all"
          ? true
          : roomCoverageFilter === "inspected"
            ? item.status !== "pending"
            : item.status === "pending";

      const matchesSearch =
        normalizedQuery.length === 0 ||
        String(item.room.room_number).toLowerCase().includes(normalizedQuery) ||
        `room ${String(item.room.room_number).toLowerCase()}`.includes(
          normalizedQuery,
        );

      return matchesStatus && matchesSearch;
    });
  }, [roomCoverageFilter, roomCoverageItems, roomSearch]);

  const selectedRoomInspections = useMemo(() => {
    if (!selectedRoom) {
      return [];
    }

    return [...roomInspections]
      .filter((inspection) => inspection.room_id === selectedRoom.id)
      .sort((left, right) => {
        const leftDate = new Date(left.inspection_date).getTime();
        const rightDate = new Date(right.inspection_date).getTime();
        return rightDate - leftDate;
      });
  }, [roomInspections, selectedRoom]);

  const selectedRoomNextDueDate = useMemo(() => {
    if (selectedRoomInspections.length === 0) {
      return new Date().toISOString().slice(0, 10);
    }

    const latestInspection = selectedRoomInspections[0];
    const nextDate = new Date(latestInspection.inspection_date);
    nextDate.setMonth(nextDate.getMonth() + 1);
    return nextDate.toISOString().slice(0, 10);
  }, [selectedRoomInspections]);

  const roomCoverageSummary = useMemo(() => {
    const inspected = roomCoverageItems.filter(
      (item) => item.status !== "pending",
    ).length;
    const pending = roomCoverageItems.filter(
      (item) => item.status === "pending",
    ).length;
    const overdue = roomCoverageItems.filter(
      (item) => item.status === "overdue",
    ).length;

    return { inspected, pending, overdue, total: roomCoverageItems.length };
  }, [roomCoverageItems]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [
        propertyData,
        roomData,
        roomInspectionData,
        kitchenInspectionData,
        laundryInspectionData,
      ] = await Promise.all([
        getProperties(),
        getRooms(),
        getRoomInspections(),
        getKitchenInspections(),
        getLaundryInspections(),
      ]);

      setProperties(propertyData);
      setRooms(roomData);
      setRoomInspections(roomInspectionData);
      setKitchenInspections(kitchenInspectionData);
      setLaundryInspections(laundryInspectionData);
      setForms({
        room: createEmptyFormState(),
        kitchen: createEmptyFormState(),
        laundry: createEmptyFormState(),
      });
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load inspection records",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const updateActiveForm = (
    updater: (current: InspectionFormState) => InspectionFormState,
  ) => {
    setForms((current) => ({
      ...current,
      [inspectionType]: updater(current[inspectionType]),
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!activeForm.targetId) {
      showAlert({
        title: "Missing target",
        message:
          "Please select a room or property before saving the inspection.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const basePayload = {
        inspection_date:
          activeForm.inspection_date || new Date().toISOString().slice(0, 10),
        inspected_by: null,
        result: activeForm.result,
        comments: activeForm.comments.trim() || null,
      };

      if (inspectionType === "room") {
        await createRoomInspection({
          ...basePayload,
          room_id: Number(activeForm.targetId),
        });
      } else if (inspectionType === "kitchen") {
        await createKitchenInspection({
          ...basePayload,
          property_id: Number(activeForm.targetId),
        });
      } else {
        await createLaundryInspection({
          ...basePayload,
          property_id: Number(activeForm.targetId),
        });
      }

      showSnackbar({
        title: `${inspectionLabels[inspectionType]} saved`,
        message:
          "The inspection has been recorded and the list has been refreshed.",
        variant: "success",
      });
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to save inspection",
        message: "Please confirm the selected target and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={3.5} sx={{ width: "100%" }}>
      {isLoading && <LinearProgress sx={{ borderRadius: 6 }} />}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          p: { xs: 2.25, md: 3 },
          boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1.1,
                borderRadius: 6,
                bgcolor: "rgba(33, 82, 184, 0.08)",
                color: "primary.main",
              }}
            >
              <FactCheckRounded fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Compliance inspections
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Record room, kitchen, and laundry checks in one place.
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              label={`Rooms: ${inspectionCadence.room} · ${roomInspections.length} records`}
              color="primary"
              variant="filled"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
            <Chip
              label={`Kitchen: ${inspectionCadence.kitchen} · ${kitchenInspections.length} records`}
              variant="outlined"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
            <Chip
              label={`Laundry: ${inspectionCadence.laundry} · ${laundryInspections.length} records`}
              variant="outlined"
              sx={{ borderRadius: 6, fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 4, p: { xs: 2.25, md: 3 } }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Add a new inspection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Open the inspection dialog to record checks for rooms, kitchens,
              or laundry areas.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              onClick={() => openModal()}
              sx={{ borderRadius: 6, fontWeight: 600, px: 2.5 }}
            >
              Add inspection
            </Button>
            <Tooltip title="Refresh records">
              <IconButton
                onClick={() => void loadData()}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 6,
                }}
              >
                <RefreshRounded />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          sx={{ mt: 2.25 }}
        >
          {(["room", "kitchen", "laundry"] as InspectionType[]).map((type) => {
            const summary = inspectionSummaries[type];
            const color =
              summary.status === "overdue"
                ? "error"
                : summary.status === "due_soon"
                  ? "warning"
                  : "success";

            return (
              <Card
                key={type}
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  borderColor: `${color}.main`,
                  bgcolor: `${color}.main` + "0A",
                }}
              >
                <CardContent sx={{ py: 1.75 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {summary.status === "overdue" ? (
                        <WarningAmberRounded color="error" fontSize="small" />
                      ) : summary.status === "due_soon" ? (
                        <ScheduleRounded color="warning" fontSize="small" />
                      ) : (
                        <CheckCircleRounded color="success" fontSize="small" />
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {inspectionLabels[type]}
                      </Typography>
                    </Stack>
                    <Chip
                      label={summary.label}
                      color={color}
                      size="small"
                      sx={{ alignSelf: "flex-start", borderRadius: 999 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Next due {formatDate(summary.nextDueDate) || "soon"}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Paper>

      <InspectionModal
        open={isModalOpen}
        inspectionType={inspectionType}
        properties={properties}
        rooms={rooms}
        formState={activeForm}
        isSubmitting={isLoading}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onInspectionTypeChange={(type) => {
          setInspectionType(type);
          setForms((current) => ({
            ...current,
            [type]: getSuggestedFormState(type),
          }));
        }}
        onTargetChange={(value) =>
          updateActiveForm((current) => ({ ...current, targetId: value }))
        }
        onDateChange={(value) =>
          updateActiveForm((current) => ({
            ...current,
            inspection_date: value,
          }))
        }
        onResultChange={(value) =>
          updateActiveForm((current) => ({ ...current, result: value }))
        }
        onCommentsChange={(value) =>
          updateActiveForm((current) => ({ ...current, comments: value }))
        }
      />

      <Paper
        variant="outlined"
        sx={{ borderRadius: 4, p: { xs: 2.25, md: 3 } }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          sx={{ mb: 2.25 }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Room coverage overview
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.35 }}
            >
              See at a glance which rooms are inspected and which still need
              attention.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              label={`${roomCoverageSummary.inspected}/${roomCoverageSummary.total} inspected`}
              color="success"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
            <Chip
              label={`${roomCoverageSummary.pending} pending`}
              color="warning"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
            <Chip
              label={`${roomCoverageSummary.overdue} overdue`}
              color="error"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          // justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Tabs
            value={roomCoverageFilter}
            onChange={(_, nextValue) => setRoomCoverageFilter(nextValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 0 }}
          >
            <Tab label="All rooms" value="all" />
            <Tab label="Inspected" value="inspected" />
            <Tab label="Pending" value="pending" />
          </Tabs>
          <TextField
            value={roomSearch}
            onChange={(event) => setRoomSearch(event.target.value)}
            placeholder="Search room"
            size="small"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { borderRadius: 999 },
            }}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {filteredRoomCoverage.map(({ room, latestInspection, status }) => {
            const isPending = status === "pending";
            const statusColor = isPending
              ? "warning"
              : status === "overdue"
                ? "error"
                : "success";

            return (
              <Card
                key={room.id}
                variant="outlined"
                sx={{ borderRadius: 3, height: "100%" }}
              >
                <CardContent>
                  <Stack spacing={1.25}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Room {room.room_number}
                      </Typography>
                      <Chip
                        label={
                          isPending
                            ? "Pending"
                            : status === "overdue"
                              ? "Overdue"
                              : "Inspected"
                        }
                        color={statusColor}
                        size="small"
                        sx={{ borderRadius: 999, fontWeight: 600 }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {latestInspection
                        ? `Last inspection ${formatDate(latestInspection.inspection_date)}`
                        : "No inspection recorded yet"}
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ mt: 0.5 }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedRoom(room)}
                        sx={{ borderRadius: 999, alignSelf: "flex-start" }}
                      >
                        View details
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openModal("room", String(room.id))}
                        sx={{ borderRadius: 999, alignSelf: "flex-start" }}
                      >
                        {isPending ? "Record inspection" : "Review inspection"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Paper>

      <Dialog
        open={Boolean(selectedRoom)}
        onClose={() => setSelectedRoom(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        {selectedRoom ? (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Room {selectedRoom.room_number}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Full inspection history and next due date for this room.
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Inspection cadence
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Room inspections follow a monthly schedule.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Chip
                        label={`Next due ${formatDate(selectedRoomNextDueDate)}`}
                        color="primary"
                        variant="filled"
                        sx={{ borderRadius: 999, fontWeight: 600 }}
                      />
                      <Chip
                        label={`${selectedRoomInspections.length} recorded inspections`}
                        variant="outlined"
                        sx={{ borderRadius: 999, fontWeight: 600 }}
                      />
                    </Stack>
                  </Stack>
                </Paper>

                {selectedRoomInspections.length > 0 ? (
                  <Stack spacing={1.5}>
                    {selectedRoomInspections.map((inspection) => (
                      <Card
                        key={inspection.id}
                        variant="outlined"
                        sx={{ borderRadius: 3 }}
                      >
                        <CardContent>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            spacing={1.5}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                              >
                                {inspection.result.replace(/_/g, " ")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.35 }}
                              >
                                {inspection.comments || "No comments recorded."}
                              </Typography>
                            </Box>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                            >
                              <Chip
                                label={
                                  formatDate(inspection.inspection_date) ||
                                  "Date pending"
                                }
                                color="secondary"
                                variant="outlined"
                                sx={{ borderRadius: 999, fontWeight: 600 }}
                              />
                              <Chip
                                label="Room inspection"
                                variant="outlined"
                                sx={{ borderRadius: 999, fontWeight: 600 }}
                              />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 6 }}>
                    No inspection history has been recorded for this room yet.
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setSelectedRoom(null)}
                color="inherit"
                sx={{ borderRadius: 999 }}
              >
                Close
              </Button>
              <Button
                onClick={() => openModal("room", String(selectedRoom.id))}
                variant="contained"
                sx={{ borderRadius: 999 }}
              >
                Add inspection
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Paper
        variant="outlined"
        sx={{ borderRadius: 4, p: { xs: 2.25, md: 3 } }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 2.25 }}
        >
          <Tabs
            value={inspectionFilter}
            onChange={(_, nextValue) => setInspectionFilter(nextValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 0 }}
          >
            <Tab label={`All (${inspectionFilterCounts.all})`} value="all" />
            <Tab
              label={`Rooms (${inspectionFilterCounts.room})`}
              value="room"
            />
            <Tab
              label={`Kitchens (${inspectionFilterCounts.kitchen})`}
              value="kitchen"
            />
            <Tab
              label={`Laundry (${inspectionFilterCounts.laundry})`}
              value="laundry"
            />
          </Tabs>
        </Stack>

        <Stack spacing={1.5}>
          {filteredInspectionHistory.length > 0 ? (
            filteredInspectionHistory.map((inspection) => (
              <Card
                key={`${inspection.type}-${inspection.id}`}
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  borderColor: "divider",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: 5,
                    bgcolor:
                      inspection.type === "room"
                        ? "primary.main"
                        : inspection.type === "kitchen"
                          ? "secondary.main"
                          : "success.main",
                  }}
                />
                <CardContent sx={{ pl: 4 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ mb: 0.75 }}
                      >
                        <Chip
                          label={inspectionLabels[inspection.type]}
                          color="primary"
                          variant="filled"
                          sx={{ borderRadius: 6, fontWeight: 700 }}
                        />
                        <Chip
                          label={inspection.result.replace(/_/g, " ")}
                          variant="outlined"
                          sx={{ borderRadius: 6, fontWeight: 600 }}
                        />
                        <Chip
                          label={inspectionCadence[inspection.type]}
                          size="small"
                          icon={<CalendarTodayRounded fontSize="small" />}
                          sx={{ borderRadius: 999, fontWeight: 600 }}
                        />
                      </Stack>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {inspection.targetLabel}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.35 }}
                      >
                        {inspectionCadence[inspection.type]} schedule · next due{" "}
                        {formatDate(getNextDueDate(inspection.type))}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                      >
                        {inspection.comments || "No comments recorded."}
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                    >
                      <Chip
                        label={
                          formatDate(inspection.inspection_date) ||
                          "Date pending"
                        }
                        color="secondary"
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                      <Chip
                        label={getDueSummary(inspection.type).label}
                        color={
                          getDueSummary(inspection.type).status === "overdue"
                            ? "error"
                            : getDueSummary(inspection.type).status ===
                                "due_soon"
                              ? "warning"
                              : "success"
                        }
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                      <Chip
                        label={
                          inspection.type === "room"
                            ? "Room"
                            : inspection.type === "kitchen"
                              ? "Kitchen"
                              : "Laundry"
                        }
                        variant="outlined"
                        sx={{ borderRadius: 6, fontWeight: 600 }}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <Alert severity="info" sx={{ borderRadius: 6 }}>
              No{" "}
              {inspectionFilter === "all"
                ? "inspection"
                : inspectionLabels[inspectionFilter].toLowerCase()}{" "}
              records yet.
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
